import React from "react";
import {
  Box,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";
import { DEFAULT_TIER_COLORS, FALLBACK_TIER_COLOR, TIER_LABEL_INK } from "../../Styles/theme";

// Literal hex on purpose: these are picked by the user, stored on the list and
// painted into exported screenshots, so they must not follow the site theme.
const COLOR_SWATCHES = [
  ...DEFAULT_TIER_COLORS,
  "#e74c3c", "#3498db", "#2ecc71", "#f39c12", "#95a5a6",
  "#e91e63", "#9c27b0", "#673ab7", "#00bcd4", "#009688", "#ff5722",
  "#795548", "#607d8b", "#ff9800", "#8bc34a", "#cddc39", "#ffffff",
];

const TierNameBlock = (props) => {
  const {
    index,
    tier,
    tierColor,
    hoveredIndex,
    setHoveredIndex,
    onEdit,
    onMoveUp,
    onMoveDown,
    isFirst,
    isLast,
    editingTier,
    setEditingTier,
    onSaveEdit,
    onDeleteTier,
    canDelete,
  } = props;

  const { t } = useTranslation();
  const isEditing = editingTier?.index === index;

  return (
    <>
      <Box
        id="tier-name-block"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          minHeight: "75px",
          minWidth: "80px",
          backgroundColor: tierColor || FALLBACK_TIER_COLOR,
          borderRadius: "4px 0 0 4px",
          padding: "0 8px",
          cursor: "pointer",
          position: "relative",
          "&:hover": { filter: "brightness(1.1)" },
        }}
        onMouseEnter={() => setHoveredIndex(index)}
        onMouseLeave={() => setHoveredIndex(null)}
        onClick={onEdit}
      >
        <Typography variant="h6" sx={{ color: TIER_LABEL_INK, fontWeight: 700 }}>
          {tier}
        </Typography>
        {hoveredIndex === index && (
          <Box
            sx={{
              position: "absolute",
              right: -28,
              display: "flex",
              flexDirection: "column",
              zIndex: 2,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <IconButton
              size="small"
              onClick={onMoveUp}
              disabled={isFirst}
              sx={{ p: 0.25, color: "var(--text-secondary)", "&:disabled": { opacity: 0.3 } }}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={onMoveDown}
              disabled={isLast}
              sx={{ p: 0.25, color: "var(--text-secondary)", "&:disabled": { opacity: 0.3 } }}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Dialog
        open={isEditing}
        onClose={() => setEditingTier(null)}
        maxWidth="xs"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>{t("tierList.editTier")}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: "16px !important" }}>
          <TextField
            label={t("tierList.tierName")}
            value={editingTier?.name || ""}
            onChange={(e) =>
              setEditingTier((prev) => (prev ? { ...prev, name: e.target.value } : prev))
            }
            fullWidth
            autoFocus
          />
          <Box>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              {t("tierList.tierColor")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {COLOR_SWATCHES.map((color) => (
                <Box
                  key={color}
                  onClick={() =>
                    setEditingTier((prev) => (prev ? { ...prev, color } : prev))
                  }
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    backgroundColor: color,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: editingTier?.color === color ? "3px solid var(--text-primary)" : "3px solid transparent",
                    transition: "border-color 0.15s",
                    "&:hover": { filter: "brightness(1.2)" },
                  }}
                >
                  {editingTier?.color === color && (
                    <CheckIcon sx={{ fontSize: 18, color: TIER_LABEL_INK }} />
                  )}
                </Box>
              ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1 }}>
              <Typography variant="body2" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                {t("tierList.customColor")}
              </Typography>
              <input
                type="color"
                value={editingTier?.color || "#666666"}
                onChange={(e) =>
                  setEditingTier((prev) => (prev ? { ...prev, color: e.target.value } : prev))
                }
                style={{
                  width: 40,
                  height: 36,
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  backgroundColor: "transparent",
                }}
              />
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: editingTier?.color || FALLBACK_TIER_COLOR,
                  border: "2px solid var(--border-strong)",
                  flexShrink: 0,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          {canDelete ? (
            <Button
              onClick={onDeleteTier}
              color="error"
              startIcon={<DeleteOutlineIcon />}
              sx={{ textTransform: "none" }}
            >
              {t("tierList.deleteTier")}
            </Button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button onClick={() => setEditingTier(null)}>{t("tierList.cancel")}</Button>
            <Button variant="contained" onClick={onSaveEdit}>
              {t("tierList.save")}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TierNameBlock;
